import type { ReactElement } from 'react';

import { TObject } from '@infinex-private/ts-utils/object';
import {
  isChainKey,
  isDefuseChainId,
  type ChainKey,
  type DefuseChainId,
} from '@infinex/chain-config';

import { emblem } from './fragments/emblem';
import { glyph } from './fragments/glyph';
import { glyphMono } from './fragments/glyphMono';

const chainKeys = TObject.keys(emblem).filter(isChainKey);
const defuseChainIds = TObject.keys(emblem).filter(isDefuseChainId);

type ChainSvgRefs = {
  /**
   * A reference to the SVG emblem `symbol` of the chain.
   */
  emblemRef: ReactElement;
  /**
   * A reference to the SVG glyph `symbol` of the chain.
   */
  glyphRef: ReactElement;
  /**
   * A reference to the monochromatic SVG glyph `symbol` of the chain.
   */
  glyphMonoRef: ReactElement;
};

type ChainSvgSymbols = {
  /**
   * A `symbol` definition for the SVG emblem of the chain.
   */
  emblemSymbol: ReactElement;
  /**
   * A `symbol` definition for the SVG glyph of the chain.
   */
  glyphSymbol: ReactElement;
  /**
   * A `symbol` definition for the monochromatic SVG glyph of the chain.
   */
  glyphMonoSymbol: ReactElement;
};

export const CHAIN_SVG_SYMBOLS: Record<ChainKey, ChainSvgSymbols> =
  TObject.fromEntries(
    chainKeys.map((key) => [
      key,
      {
        emblemSymbol: emblem[key],
        glyphSymbol: glyph[key],
        glyphMonoSymbol: glyphMono[key],
      },
    ])
  );

export const DEFUSE_SVG_SYMBOLS: Record<DefuseChainId, ChainSvgSymbols> =
  TObject.fromEntries(
    defuseChainIds.map((key) => [
      key,
      {
        emblemSymbol: emblem[key],
        glyphSymbol: glyph[key],
        glyphMonoSymbol: glyphMono[key],
      },
    ])
  );

/**
 * @private Exported exclusively for use in `@infinex/ui/components/emblem`.
 */
export const CHAIN_SVG_REFS: Record<ChainKey, ChainSvgRefs> =
  TObject.fromEntries(
    chainKeys.map((chain) => [
      chain,
      {
        emblemRef: <Reference chain={chain} type="emblemSymbol" />,
        glyphRef: <Reference chain={chain} type="glyphSymbol" />,
        glyphMonoRef: <Reference chain={chain} type="glyphMonoSymbol" />,
      },
    ])
  );

/**
 * @private Exported exclusively for use in `@infinex/ui/components/emblem`.
 */
export const DEFUSE_SVG_REFS: Record<DefuseChainId, ChainSvgRefs> =
  TObject.fromEntries(
    defuseChainIds.map((chain) => [
      chain,
      {
        emblemRef: <Reference chain={chain} type="emblemSymbol" />,
        glyphRef: <Reference chain={chain} type="glyphSymbol" />,
        glyphMonoRef: <Reference chain={chain} type="glyphMonoSymbol" />,
      },
    ])
  );

/**
 * A reference to the `<symbol>` elements defined in `ChainSvgDefs`.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Element/symbol
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Element/use
 */
type ReferenceProps = {
  chain: ChainKey | DefuseChainId;
  type: keyof ChainSvgSymbols;
};
function Reference(props: ReferenceProps) {
  const { chain, type } = props;
  const id = makeSymbolId(chain, type);

  return (
    <svg viewBox="0 0 24 24" height={24} width={24}>
      <use href={`#${id}`} />
    </svg>
  );
}

/**
 * Make a unique ID for chain SVG assets, which is stable between `<symbol>`
 * and `<use>` elements.
 *
 * @param chain - The chain key.
 * @param type - The type of symbol to make.
 */
export function makeSymbolId(
  chain: ChainKey | DefuseChainId,
  type: keyof ChainSvgSymbols
) {
  return `chain-${chain}-${type}`;
}
